import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { recordNewsletterMetric } from '@/lib/newsletterMetrics';
import { useParams, Link } from 'react-router-dom';
import {
  newsletterApi,
  type Issue,
  type TLDRItem,
  type NewsItem,
  type CompanyNewsItem,
} from '@/lib/api/newsletter';
import { SubscribeForm } from '@/components/newsletter/SubscribeForm';
import { SignalContent } from '@/components/newsletter/SignalContent';
import PageMeta from '@/components/PageMeta';

export default function Numero() {
  const { numero } = useParams<{ numero: string }>();
  const issueNumber = parseInt(numero || '0', 10);

  const {
    data: issue,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['issue-it', issueNumber],
    queryFn: async (): Promise<Issue | null> => {
      return await newsletterApi.getIssueByNumber(issueNumber);
    },
    enabled: issueNumber > 0,
    staleTime: 1000 * 60 * 30,
  });

  const publishedDate = issue?.published_at
    ? new Date(issue.published_at).toLocaleDateString('it-IT', {
        timeZone: 'Europe/Rome',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  useEffect(() => {
    if (!issue) return;
    const view = () => recordNewsletterMetric(issue, 'view');
    view();
    window.addEventListener('newsletter-consent-change', view);
    document.addEventListener('visibilitychange', view);
    return () => {
      window.removeEventListener('newsletter-consent-change', view);
      document.removeEventListener('visibilitychange', view);
    };
  }, [issue]);

  return (
    <div
      style={{
        fontFamily: "'DM Sans', sans-serif",
        color: '#E8E6E1',
        background: '#0A0A0B',
        minHeight: '100vh',
      }}
    >
      <PageMeta
        title={
          issue
            ? `${issue.title} — Segnali AI #${issue.issue_number}`
            : 'Segnali AI'
        }
        description={
          issue?.subtitle ||
          'Il briefing di Luciano Ballerano su AI, lavoro e imprese.'
        }
        path={`/numero/${issueNumber}`}
      />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@400;700;900&display=swap"
        rel="stylesheet"
      />

      {/* Header */}
      <header
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid rgba(26,26,46,0.06)',
          position: 'sticky',
          top: 0,
          background: 'rgba(10,10,11,0.95)',
          backdropFilter: 'blur(20px)',
          zIndex: 100,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration: 'none',
              color: '#E8E6E1',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <img
              src="/logo-lb3.png"
              alt="LB"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                objectFit: 'contain',
              }}
            />
            <span
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                fontSize: 20,
              }}
            >
              Segnali AI
            </span>
          </Link>
          <div style={{ display: 'flex', gap: 12 }}>
            <Link
              to="/archivio"
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: 'rgba(26,26,46,0.06)',
                color: '#B8BECC',
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Archivio
            </Link>
            <Link
              to="/"
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: 'rgba(59,130,246,0.1)',
                color: '#3B82F6',
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Sito
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main
        style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px' }}
        onClick={(e) => {
          const anchor = (e.target as Element).closest(
            'a[href]',
          ) as HTMLAnchorElement | null;
          if (
            e.isTrusted &&
            issue &&
            anchor &&
            /^https?:/i.test(anchor.getAttribute('href') || '') &&
            anchor.origin !== window.location.origin
          )
            recordNewsletterMetric(
              issue,
              'source_click',
              anchor.getAttribute('href')!,
            );
        }}
        onAuxClick={(e) => {
          const anchor = (e.target as Element).closest(
            'a[href]',
          ) as HTMLAnchorElement | null;
          if (
            e.isTrusted &&
            e.button === 1 &&
            issue &&
            anchor &&
            /^https?:/i.test(anchor.getAttribute('href') || '') &&
            anchor.origin !== window.location.origin
          )
            recordNewsletterMetric(
              issue,
              'source_click',
              anchor.getAttribute('href')!,
            );
        }}
      >
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 24, marginBottom: 12 }}>{'\u23F3'}</div>
            <p style={{ color: 'rgba(232,230,225,0.4)' }}>Caricamento...</p>
          </div>
        )}

        {error && (
          <div
            style={{
              textAlign: 'center',
              padding: 60,
              background: 'rgba(239,68,68,0.1)',
              borderRadius: 16,
              border: '1px solid rgba(239,68,68,0.2)',
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 12 }}>
              {'\u26A0\uFE0F'}
            </div>
            <p style={{ color: '#ef4444' }}>
              Errore nel caricamento del numero
            </p>
          </div>
        )}

        {!isLoading && !error && !issue && (
          <div
            style={{
              textAlign: 'center',
              padding: 60,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 12 }}>
              {'\uD83D\uDD2D'}
            </div>
            <p style={{ color: 'rgba(232,230,225,0.4)' }}>Numero non trovato</p>
            <Link
              to="/archivio"
              style={{
                color: '#3B82F6',
                textDecoration: 'none',
                marginTop: 12,
                display: 'inline-block',
              }}
            >
              {`Vai all'archivio \u2192`}
            </Link>
          </div>
        )}

        {issue && (
          <>
            {/* Header */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <span style={{ fontSize: 13, color: 'rgba(232,230,225,0.4)' }}>
                  Numero #{issue.issue_number}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.15)' }}>
                  {'\u00B7'}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: 'rgba(232,230,225,0.4)',
                    textTransform: 'capitalize',
                  }}
                >
                  {publishedDate}
                </span>
              </div>
              <h1
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                  fontWeight: 700,
                  marginBottom: 12,
                  lineHeight: 1.2,
                }}
              >
                {issue.title}
              </h1>
              {issue.subtitle && (
                <p style={{ fontSize: 18, color: '#a1a1b5', lineHeight: 1.6 }}>
                  {issue.subtitle}
                </p>
              )}
            </div>

            <SignalContent issue={issue} />
            {/* TL;DR Section */}
            {issue.tldr && issue.tldr.length > 0 && (
              <section
                style={{
                  marginBottom: 48,
                  padding: 28,
                  borderRadius: 16,
                  background: 'rgba(59,130,246,0.08)',
                  border: '1px solid rgba(59,130,246,0.2)',
                }}
              >
                <h2
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#3B82F6',
                    letterSpacing: '0.06em',
                    marginBottom: 20,
                  }}
                >
                  TL;DR
                </h2>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  {issue.tldr.map((item: TLDRItem, i: number) => (
                    <div key={i} style={{ display: 'flex', gap: 12 }}>
                      <span
                        style={{
                          color: '#3B82F6',
                          flexShrink: 0,
                          marginTop: 3,
                        }}
                      >
                        {'\u25B8'}
                      </span>
                      <div>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: 1.7,
                            color: '#D1D1E0',
                          }}
                        >
                          {item.text}
                        </p>
                        {item.tags && item.tags.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              gap: 6,
                              marginTop: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            {item.tags.map((tag, j) => (
                              <span
                                key={j}
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: 6,
                                  background: 'rgba(59,130,246,0.15)',
                                  fontSize: 11,
                                  color: '#60A5FA',
                                  fontWeight: 500,
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Market Updates */}
            {issue.market_updates && issue.market_updates.length > 0 && (
              <section style={{ marginBottom: 48 }}>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: "'Playfair Display', serif",
                    marginBottom: 20,
                  }}
                >
                  Mercato e imprese
                </h2>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
                >
                  {issue.market_updates.map((item: NewsItem, i: number) => (
                    <div
                      key={i}
                      style={{
                        padding: 24,
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: 17,
                          fontWeight: 600,
                          marginBottom: 8,
                          color: '#E8E6E1',
                        }}
                      >
                        {item.headline}
                      </h3>
                      <p
                        style={{
                          fontSize: 14.5,
                          lineHeight: 1.7,
                          color: '#9CA3AF',
                        }}
                      >
                        {item.summary}
                      </p>
                      {item.source_url && (
                        <a
                          href={item.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            marginTop: 12,
                            fontSize: 13,
                            color: '#3B82F6',
                            textDecoration: 'none',
                          }}
                        >
                          {`Fonte \u2192`}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Research Section */}
            {issue.research_section && issue.research_section.length > 0 && (
              <section style={{ marginBottom: 48 }}>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: "'Playfair Display', serif",
                    marginBottom: 20,
                  }}
                >
                  Ricerca e sviluppo
                </h2>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
                >
                  {issue.research_section.map((item: NewsItem, i: number) => (
                    <div
                      key={i}
                      style={{
                        padding: 24,
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: 17,
                          fontWeight: 600,
                          marginBottom: 8,
                          color: '#E8E6E1',
                        }}
                      >
                        {item.headline}
                      </h3>
                      <p
                        style={{
                          fontSize: 14.5,
                          lineHeight: 1.7,
                          color: '#9CA3AF',
                        }}
                      >
                        {item.summary}
                      </p>
                      {item.source_url && (
                        <a
                          href={item.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            marginTop: 12,
                            fontSize: 13,
                            color: '#3B82F6',
                            textDecoration: 'none',
                          }}
                        >
                          {`Fonte \u2192`}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Company News */}
            {issue.company_news && issue.company_news.length > 0 && (
              <section style={{ marginBottom: 48 }}>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: "'Playfair Display', serif",
                    marginBottom: 20,
                  }}
                >
                  Notizie dalle aziende
                </h2>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                >
                  {issue.company_news.map(
                    (company: CompanyNewsItem, i: number) => (
                      <div key={i}>
                        <h3
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: '#F59E0B',
                            marginBottom: 12,
                          }}
                        >
                          {company.company}
                        </h3>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                          }}
                        >
                          {company.items.map((item, j) => (
                            <div
                              key={j}
                              style={{
                                padding: 20,
                                borderRadius: 12,
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.08)',
                              }}
                            >
                              <h4
                                style={{
                                  fontSize: 15,
                                  fontWeight: 600,
                                  marginBottom: 6,
                                  color: '#E8E6E1',
                                }}
                              >
                                {item.headline}
                              </h4>
                              <p
                                style={{
                                  fontSize: 14,
                                  lineHeight: 1.6,
                                  color: '#9CA3AF',
                                }}
                              >
                                {item.summary}
                              </p>
                              {item.source_url && (
                                <a
                                  href={item.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-block',
                                    marginTop: 10,
                                    fontSize: 13,
                                    color: '#3B82F6',
                                    textDecoration: 'none',
                                  }}
                                >
                                  {`Fonte \u2192`}
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </section>
            )}

            {/* Subscribe CTA */}
            <section
              style={{
                marginTop: 60,
                padding: 40,
                borderRadius: 20,
                background: 'rgba(245,158,11,0.05)',
                border: '1px solid rgba(245,158,11,0.15)',
                textAlign: 'center',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 22,
                  fontWeight: 700,
                  marginBottom: 10,
                }}
              >
                Ti è piaciuto questo numero?
              </h3>
              <p style={{ fontSize: 15, color: '#9CA3AF', marginBottom: 24 }}>
                Iscriviti per ricevere Segnali AI ogni settimana nella tua
                inbox.
              </p>
              <SubscribeForm variant="large" />
            </section>
          </>
        )}
      </main>
    </div>
  );
}
